import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Anchor, Skeleton, Stack, Text } from "@mantine/core"
import { formatInteger } from "../../lib/format-date"
import { SearchHitCard } from "../SearchHitCard"
import type { IProps } from "./interfaces"

import classes from "./styles.module.css"

/** Skeleton placeholders while the engine loads / warms — honest card shape, not
 * a bare spinner, so the layout doesn't jump when real hits arrive. */
const SKELETON_KEYS = ["a", "b", "c"] as const

/**
 * The "Moments" group on the dashboard — engine content hits rendered below the
 * title-filtered meeting list. Pure presentation: the smart wrapper owns the
 * query, the API call, and the warming-retry polling, and maps its status onto
 * {@link TMomentsState}. Deep results (the full surface) stay on `/search`.
 */
export const MomentsSection = ({
  query,
  state,
  hits,
  engine,
  warmingLong = false,
  errorMessage,
  onRetry,
}: IProps): React.JSX.Element => {
  const { i18n } = useLingui()
  const count = state === "results" ? hits.length : null

  return (
    <section className={classes.wrap} aria-label={i18n._(msg`Moments`)}>
      <div className={classes.head}>
        <span className={`${classes.label} lex-eyebrow`}>
          <Trans>Moments</Trans>
        </span>
        {count !== null && <span className={classes.count}>{count}</span>}
        <span className={classes.rule} />
        {state === "results" && engine && count !== null && count > 0 && (
          <Text size="xs" c="dimmed" className={classes.engine}>
            <Trans>searched {formatInteger(i18n, engine.chunkCount)} passages</Trans>
          </Text>
        )}
      </div>

      {(state === "loading" || state === "warming") && (
        <Stack gap="xs">
          {state === "warming" && (
            <Text size="sm" c="dimmed">
              {warmingLong ? (
                <Trans>This is taking longer than usual — we'll show results as soon as it's ready.</Trans>
              ) : (
                <Trans>Warming your search index…</Trans>
              )}
            </Text>
          )}
          <div className={classes.skeletons}>
            {SKELETON_KEYS.map((key) => (
              <Skeleton key={key} height={92} radius="md" />
            ))}
          </div>
        </Stack>
      )}

      {state === "unavailable" && (
        <Text size="sm" c="dimmed" className={classes.note}>
          <Trans>Search is temporarily unavailable.</Trans>{" "}
          <Anchor component="button" type="button" onClick={onRetry}>
            <Trans>Try again</Trans>
          </Anchor>
        </Text>
      )}

      {state === "error" && (
        <Text size="sm" c="dimmed" className={classes.note}>
          {errorMessage ?? i18n._(msg`Couldn't load moments.`)}{" "}
          <Anchor component="button" type="button" onClick={onRetry}>
            <Trans>Try again</Trans>
          </Anchor>
        </Text>
      )}

      {state === "results" && hits.length === 0 && (
        <Text size="sm" c="dimmed" className={classes.note}>
          <Trans>No moments matched “{query}”.</Trans>
        </Text>
      )}

      {state === "results" && hits.length > 0 && (
        <div className={classes.hitList}>
          {hits.map((hit) => (
            <SearchHitCard key={hit.chunkId} hit={hit} query={query} />
          ))}
        </div>
      )}
    </section>
  )
}
